import React, { useState, useEffect } from "react";
import axios from "axios";
import "./AdminEmailManagement.css";

// Dropdown options: single role selection
const ROLE_OPTIONS = [
  { value: "Fotograaf", label: "Fotograaf", roles: ["Fotograaf"] },
  { value: "HQ", label: "HQ", roles: ["HQ"] },
];

const AdminEmailManagement = () => {
  const [emails, setEmails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [currentEmail, setCurrentEmail] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Form state - Fotograaf pre-selected
  const [formData, setFormData] = useState({
    email: "",
    roles: ["Fotograaf"],
  });

  const API_BASE_URL = "https://sgoolfoto-backend.onrender.com/api/admin";

  // Fetch emails from backend on component mount
  useEffect(() => {
    fetchEmails();
  }, []);

  // Fetch all emails from CSV backend
  const fetchEmails = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE_URL}/emails`);
      
      if (response.data.success) {
        setEmails(response.data.data || []);
      }
    } catch (error) {
      console.error("Error fetching emails:", error);
      setErrorMessage("Failed to load emails from server");
    } finally {
      setLoading(false);
    }
  };

  // Validate email format
  const isValidEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Handle Add New - Fotograaf pre-selected
  const handleAddNew = () => {
    setCurrentEmail(null);
    setFormData({
      email: "",
      roles: ["Fotograaf"],
    });
    setErrorMessage("");
    setShowModal(true);
  };

  // Handle Edit
  const handleEdit = (email) => {
    setCurrentEmail(email);
    setFormData({
      email: email.email,
      roles: Array.isArray(email.roles) && email.roles.length > 0 ? [...email.roles] : ["Fotograaf"],
    });
    setErrorMessage("");
    setShowModal(true);
  };

  // Get dropdown value from roles array (single role)
  const getRolesDropdownValue = (roles) => {
    if (!Array.isArray(roles) || roles.length === 0) return "Fotograaf";
    return roles.includes("HQ") ? "HQ" : "Fotograaf";
  };

  // Handle role dropdown change
  const handleRoleChange = (e) => {
    const option = ROLE_OPTIONS.find((o) => o.value === e.target.value);
    setFormData((prev) => ({ ...prev, roles: option ? option.roles : ["Fotograaf"] }));
  };

  // Handle Delete Click
  const handleDeleteClick = (id) => {
    setDeleteId(id);
    setShowDeleteModal(true);
  };

  // Handle Submit (Add/Edit)
  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage("");
    
    if (!formData.email) {
      setErrorMessage("E-mail is verplicht");
      return;
    }

    // Validate email format
    if (!isValidEmail(formData.email)) {
      setErrorMessage("Ongeldig e-mailformaat");
      return;
    }

    const normalizedEmail = formData.email.toLowerCase().trim();

    try {
      setSubmitting(true);
      
      if (currentEmail) {
        // Update existing email via API
        const response = await axios.put(
          `${API_BASE_URL}/emails/${currentEmail.id}`,
          { email: normalizedEmail, roles: formData.roles }
        );
        
        if (response.data.success) {
          // Refetch to ensure table shows saved data (including roles)
          await fetchEmails();
          setShowModal(false);
          setFormData({ email: "", roles: ["Fotograaf"] });
        }
      } else {
        // Add new email via API - Fotograaf pre-selected
        const response = await axios.post(
          `${API_BASE_URL}/emails`,
          { email: normalizedEmail, roles: formData.roles }
        );
        
        if (response.data.success) {
          // Refetch to ensure table shows saved data (including roles)
          await fetchEmails();
          setShowModal(false);
          setFormData({ email: "", roles: ["Fotograaf"] });
        }
      }
    } catch (error) {
      console.error("Error submitting email:", error);
      
      if (error.response?.status === 409) {
        setErrorMessage("E-mail bestaat al");
      } else if (error.response?.status === 400) {
        setErrorMessage(error.response.data.error || "Ongeldig e-mailformaat");
      } else {
        setErrorMessage(error.response?.data?.error || "Opslaan van e-mail mislukt");
      }
    } finally {
      setSubmitting(false);
    }
  };

  // Handle Delete Confirm
  const handleDeleteConfirm = async () => {
    try {
      setSubmitting(true);
      const response = await axios.delete(`${API_BASE_URL}/emails/${deleteId}`);
      
      if (response.data.success) {
        // Update local state immediately by filtering out deleted email
        setEmails(emails.filter(email => email.id !== deleteId));
        setShowDeleteModal(false);
        setDeleteId(null);
      }
    } catch (error) {
      console.error("Error deleting email:", error);
      setErrorMessage(error.response?.data?.error || "Verwijderen van e-mail mislukt");
      setShowDeleteModal(false);
    } finally {
      setSubmitting(false);
    }
  };

  // Filter emails based on search
  const filteredEmails = emails.filter((email) =>
    email.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return "N/B";
    const date = new Date(dateString);
    return date.toLocaleDateString("nl-NL", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  return (
    <div className="admin-email-management">
      <div className="management-header">
        <div className="header-left">
          <h2>E-mailbeheer</h2>
        </div>
        <button onClick={handleAddNew} className="btn-add">
          ➕ Nieuw E-mailadres Toevoegen
        </button>
      </div>

      {errorMessage && (
        <div className="alert-error">
          <span>⚠️ {errorMessage}</span>
          <button onClick={() => setErrorMessage("")} className="alert-close">
            ✕
          </button>
        </div>
      )}

      <div className="management-controls">
        <div className="search-box">
          <span className="search-icon">🔍</span>
          <input
            type="text"
            placeholder="Zoeken op e-mail..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="stats">
          <span className="stat-item">
            Totaal: <strong>{emails.length}</strong>
          </span>
        </div>
      </div>

      <div className="table-container">
        {loading ? (
          <div className="table-loading">
            <div className="spinner"></div>
            <p>E-mails laden...</p>
          </div>
        ) : (
          <table className="email-table">
            <thead>
              <tr>
                <th>Nr.</th>
                <th>E-mailadres</th>
                <th>Rollen</th>
                <th>Aanmaakdatum</th>
                <th>Laatst Bijgewerkt</th>
                <th>Acties</th>
              </tr>
            </thead>
            <tbody>
              {filteredEmails.length > 0 ? (
                filteredEmails.map((email, index) => (
                  <tr key={email.id}>
                    <td>{index + 1}</td>
                    <td>
                      <div className="email-cell">
                        <div className="email-avatar-small">
                          {email.email.charAt(0).toUpperCase()}
                        </div>
                        <span>{email.email}</span>
                      </div>
                    </td>
                    <td>
                      <span className="roles-badges">
                        {(Array.isArray(email.roles) ? email.roles : ["Fotograaf"]).map((r) => (
                          <span key={r} className="role-badge">{r}</span>
                        ))}
                      </span>
                    </td>
                    <td>{formatDate(email.createdAt)}</td>
                    <td>{formatDate(email.updatedAt)}</td>
                    <td>
                      <div className="action-buttons">
                        <button
                          onClick={() => handleEdit(email)}
                          className="btn-action btn-edit"
                          title="Bewerken"
                        >
                          ✏️
                        </button>
                        <button
                          onClick={() => handleDeleteClick(email.id)}
                          className="btn-action btn-delete"
                          title="Verwijderen"
                        >
                          🗑️
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="6" className="no-data">
                    {searchTerm ? "Geen e-mails gevonden die overeenkomen met uw zoekopdracht" : "Geen e-mails gevonden. Voeg uw eerste e-mail toe!"}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </div>

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content-form" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setShowModal(false)}>
              ✕
            </button>
            <div className="modal-header">
              <h2>{currentEmail ? "E-mail Bewerken" : "Nieuw E-mailadres Toevoegen"}</h2>
              <p>
                {currentEmail
                  ? "E-mailadres bijwerken"
                  : "Voeg een nieuw e-mailadres toe aan het systeem"}
              </p>
            </div>

            <form onSubmit={handleSubmit} className="email-form">
              <div className="form-group">
                <label htmlFor="email">
                  E-mailadres <span className="required">*</span>
                </label>
                <input
                  type="email"
                  id="email"
                  value={formData.email}
                  onChange={(e) => {
                    setFormData({ ...formData, email: e.target.value });
                    setErrorMessage("");
                  }}
                  placeholder="E-mail invoeren"
                  required
                  disabled={submitting}
                />
              </div>

              <div className="form-group">
                <label htmlFor="roles">Rollen</label>
                <select
                  id="roles"
                  value={getRolesDropdownValue(formData.roles)}
                  onChange={handleRoleChange}
                  disabled={submitting}
                  className="role-select"
                >
                  {ROLE_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select><br></br>
                <small>Fotograaf is standaard geselecteerd.</small>
              </div>

              {errorMessage && <p className="error-text">{errorMessage}</p>}

              <div className="form-actions">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="btn-cancel"
                  disabled={submitting}
                >
                  Annuleren
                </button>
                <button type="submit" className="btn-submit" disabled={submitting}>
                  {submitting
                    ? "Opslaan..."
                    : currentEmail
                    ? "E-mail Bijwerken"
                    : "E-mail Toevoegen"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="modal-overlay" onClick={() => !submitting && setShowDeleteModal(false)}>
          <div className="modal-content-confirm" onClick={(e) => e.stopPropagation()}>
            <div className="confirm-icon">⚠️</div>
            <h2>Verwijderen Bevestigen</h2>
            <p>Weet u zeker dat u dit e-mailadres wilt verwijderen?</p>
            <p className="warning-text">Deze actie kan niet ongedaan worden gemaakt.</p>
            <div className="confirm-actions">
              <button
                onClick={() => setShowDeleteModal(false)}
                className="btn-cancel"
                disabled={submitting}
              >
                Annuleren
              </button>
              <button 
                onClick={handleDeleteConfirm} 
                className="btn-delete-confirm"
                disabled={submitting}
              >
                {submitting ? "Verwijderen..." : "Verwijderen"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminEmailManagement;

